// modules/requerimiento/components/DevolucionPlacaModal.jsx
// "Devolver equipos" de un vehículo, con el diseño de los diálogos de la app: tipo de devolución,
// sucursal y los equipos como tarjetas que se eligen; cada elegido lleva observación y foto.
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, Alert, CircularProgress,
  Select, MenuItem, TextField, Stack, IconButton, FormControl,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import { styled }          from "@mui/material/styles";
import CloseIcon           from "@mui/icons-material/Close";
import AddAPhotoOutlinedIcon from "@mui/icons-material/AddAPhotoOutlined";
import DeleteOutlineIcon   from "@mui/icons-material/DeleteOutline";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import MemoryIcon          from "@mui/icons-material/Memory";
import { getModemsByPlaca } from "../../services/devolucion";

// ── Constantes fuera del componente ──────────────────────────
const SUCURSALES = [
  "La Paz(El Alto)", "La Paz", "Cochabamba", "Santa Cruz",
  "Oruro", "Chuquisaca", "Pando", "Beni", "Tarija", "Potosi", "Yacuiba",
];

const AZUL = "#0284C7";
const AZUL_OSCURO = "#075985";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)", clipPath: "inset(50%)",
  height: 1, overflow: "hidden", position: "absolute",
  bottom: 0, left: 0, whiteSpace: "nowrap", width: 1,
});

// ── FotoField — con cleanup de object URL ─────────────────────
function FotoField({ id, file, onChange, onClear }) {
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!file) { setPreview(""); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url); // cleanup al desmontar o cambiar file
  }, [file]);

  if (!file) {
    return (
      <Button
        component="label"
        variant="outlined"
        startIcon={<AddAPhotoOutlinedIcon />}
        sx={{ textTransform: "none", alignSelf: "flex-start" }}
      >
        Agregar foto (opcional)
        <VisuallyHiddenInput id={id} type="file" accept="image/*" onChange={onChange} />
      </Button>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        component="img"
        src={preview}
        alt="Foto del equipo"
        sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: "8px", border: 1, borderColor: "divider" }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap>{file.name}</Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Button component="label" size="small" sx={{ textTransform: "none", px: 0, minWidth: 0 }}>
            Cambiar
            <VisuallyHiddenInput type="file" accept="image/*" onChange={onChange} />
          </Button>
          <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onClear} sx={{ textTransform: "none" }}>
            Quitar
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}

function getClientes(modem) {
  return Array.isArray(modem?.clientes) ? modem.clientes.filter(Boolean) : [];
}

// ── Componente principal ──────────────────────────────────────
function getConfirmLabel(tipoDevolucion) {
  if (tipoDevolucion === "2") return "Confirmar desactivación";
  return "Confirmar devolución";
}

export default function DevolucionPlacaModal({
  open, placa, cliente, serv,
  onClose, onSave,
  defaultSucursal, saving = false,
}) {
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [sucursal,        setSucursal]        = useState("");
  const [modems,          setModems]          = useState([]);
  const [tipoDevolucion,  setTipoDevolucion]  = useState("1");
  const [selectedModems,  setSelectedModems]  = useState(new Set());
  const [observaciones,   setObservaciones]   = useState({});
  const [imagenes,        setImagenes]        = useState({});

  // FIX: async/await en lugar de .then/.catch/.finally
  useEffect(() => {
    if (!open || !placa) return;
    if (!serv) { setError("El servicio es requerido"); return; }

    let cancelled = false;

    const fetchModems = async () => {
      setLoading(true);
      setError(null);
      setModems([]);
      setSelectedModems(new Set());
      setObservaciones({});
      setImagenes({});
      setTipoDevolucion("1");
      setSucursal((prev) => prev || defaultSucursal || "");

      try {
        const { data } = await getModemsByPlaca(placa, serv);
        if (!cancelled) setModems(Array.isArray(data) ? data : data?.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || "Error al cargar módems");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchModems();
    return () => { cancelled = true; };
  }, [open, placa, serv, defaultSucursal]);

  const handleToggleModem = useCallback((num_sim) => {
    if (tipoDevolucion === "2") return;
    const id = String(num_sim);
    setSelectedModems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setObservaciones((o) => { const n = { ...o }; delete n[id]; return n; });
        setImagenes((img) => { const n = { ...img }; delete n[id]; return n; });
      } else {
        next.add(id);
      }
      return next;
    });
  }, [tipoDevolucion]);

  useEffect(() => {
    if (tipoDevolucion !== "2") return;
    setSelectedModems(new Set(modems.map((modem) => String(modem.num_sim)).filter(Boolean)));
  }, [modems, tipoDevolucion]);

  const soloClienteDisponible = useMemo(
    () =>
      modems.some((modem) => {
        const cantidadClientes = Number(modem?.cantidad_clientes ?? getClientes(modem).length);
        return modem?.multiple_clientes || cantidadClientes > 1;
      }),
    [modems]
  );

  useEffect(() => {
    if (tipoDevolucion !== "2" || soloClienteDisponible) return;
    setTipoDevolucion("1");
    setSelectedModems(new Set());
  }, [soloClienteDisponible, tipoDevolucion]);

  const handleObservacionChange = useCallback((num_sim, value) => {
    setObservaciones((prev) => ({ ...prev, [String(num_sim)]: value }));
  }, []);

  const handleImagenChange = useCallback((num_sim, e) => {
    const file = e.target.files?.[0];
    if (file) setImagenes((prev) => ({ ...prev, [String(num_sim)]: file }));
  }, []);

  const noModems  = !loading && !error && modems.length === 0;
  // En "Solo cliente" el equipo no se mueve: no se pide sucursal y conserva la que ya tiene.
  const pideSucursal = tipoDevolucion !== "2";
  const canConfirm = !loading && !error && (!pideSucursal || sucursal !== "") && !!tipoDevolucion && selectedModems.size > 0 && !saving;

  const handleConfirm = () => {
    const filteredObs = {};
    const filteredImg = {};
    selectedModems.forEach((id) => {
      filteredObs[id] = observaciones[id] || "";
      filteredImg[id] = imagenes[id] || null;
    });
    onSave?.({
      tipoDevolucion,
      selectedModems: Array.from(selectedModems),
      placa,
      sucursal: pideSucursal ? sucursal : "",
      observaciones: filteredObs,
      imagenes: filteredImg,
    });
  };

  const soloCliente = tipoDevolucion === "2";
  const listo = !loading && !error && !noModems;
  const todosElegidos = modems.length > 0 && modems.every((m) => selectedModems.has(String(m.num_sim)));

  const handleTipoChange = (nextTipo) => {
    if (!nextTipo || nextTipo === tipoDevolucion) return;
    if (nextTipo === "2" && !soloClienteDisponible) return;
    setTipoDevolucion(nextTipo);
    if (nextTipo === "2") {
      setSelectedModems(new Set(modems.map((modem) => String(modem.num_sim)).filter(Boolean)));
    } else {
      setSelectedModems(new Set());
    }
  };

  const handleElegirTodos = () => {
    if (soloCliente) return;
    if (todosElegidos) {
      setSelectedModems(new Set());
      setObservaciones({});
      setImagenes({});
    } else {
      setSelectedModems(new Set(modems.map((m) => String(m.num_sim)).filter(Boolean)));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="devolucion-dialog-title"
      PaperProps={{ sx: { borderRadius: "12px", height: { sm: "88vh" } } }}
    >
      {/* Cabecera */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: "8px", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0,
            background: `linear-gradient(135deg, ${AZUL}, ${AZUL_OSCURO})`,
          }}
        >
          <AssignmentReturnIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }} id="devolucion-dialog-title">
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
            Devolver equipos del vehículo
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>{placa || "—"}</Typography>
          {cliente && <Typography variant="caption" color="text.secondary" noWrap component="div">{cliente}</Typography>}
        </Box>
        <IconButton aria-label="Cerrar" onClick={onClose} disabled={saving} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers sx={{ px: 2.5 }}>
        {loading ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <CircularProgress size={26} />
            <Typography variant="body2" color="text.secondary">Buscando los equipos del vehículo…</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : noModems ? (
          <Stack alignItems="center" spacing={0.75} sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            <MemoryIcon sx={{ fontSize: 40, opacity: 0.5 }} />
            <Typography sx={{ fontWeight: 700, color: "text.primary" }}>Sin equipos activos</Typography>
            <Typography variant="body2">Este vehículo no tiene módems activos para devolver.</Typography>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {/* Tipo de devolución */}
            <Box>
              <Etiqueta>Tipo de devolución</Etiqueta>
              <ToggleButtonGroup
                exclusive
                fullWidth
                value={tipoDevolucion}
                onChange={(_, v) => handleTipoChange(v)}
                sx={{ "& .MuiToggleButton-root": { textTransform: "none", py: 1, flexDirection: "column", alignItems: "flex-start", textAlign: "left" } }}
              >
                <ToggleButton value="1">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Físico</Typography>
                  <Typography variant="caption" color="text.secondary">Se retira el equipo del vehículo</Typography>
                </ToggleButton>
                <ToggleButton value="2" disabled={!soloClienteDisponible}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Solo cliente</Typography>
                  <Typography variant="caption" color="text.secondary">Se quita este cliente; el equipo sigue donde está</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
              {!soloClienteDisponible && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  "Solo cliente" se habilita cuando el módem está asociado a más de un cliente.
                </Typography>
              )}
            </Box>

            {/* Sucursal: solo en la devolución física */}
            {pideSucursal && (
            <Box>
              <Etiqueta>Sucursal de devolución</Etiqueta>
              <FormControl fullWidth size="small" error={sucursal === ""}>
                <Select
                  value={sucursal}
                  displayEmpty
                  onChange={(e) => setSucursal(e.target.value)}
                  renderValue={(v) => v || <span style={{ opacity: 0.6 }}>Elige dónde se entrega el equipo…</span>}
                >
                  {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            )}

            {/* Equipos */}
            <Box>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Etiqueta sx={{ mb: 0, flex: 1 }}>
                  {soloCliente ? `Equipos (${modems.length})` : `Elige los equipos (${modems.length})`}
                </Etiqueta>
                {!soloCliente && modems.length > 1 && (
                  <Button size="small" onClick={handleElegirTodos} sx={{ textTransform: "none" }}>
                    {todosElegidos ? "Quitar todos" : "Elegir todos"}
                  </Button>
                )}
              </Stack>
              {soloCliente && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  En "Solo cliente" se incluyen todos los equipos del vehículo.
                </Typography>
              )}
              <Stack spacing={1}>
                {modems.map((modem) => {
                  const id = String(modem.num_sim);
                  const checked = selectedModems.has(id);
                  const clientes = getClientes(modem);
                  const servicio = modem.cod_servicio_cliente ?? modem.cod_servicio;
                  return (
                    <Box
                      key={id}
                      sx={{
                        borderRadius: "10px", border: "1px solid", overflow: "hidden",
                        borderColor: checked ? AZUL : "divider",
                        bgcolor: checked ? "rgba(2,132,199,0.05)" : "transparent",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        onClick={() => handleToggleModem(modem.num_sim)}
                        sx={{ p: 1.5, cursor: soloCliente ? "default" : "pointer" }}
                      >
                        {checked
                          ? <CheckCircleIcon sx={{ color: AZUL, flexShrink: 0 }} />
                          : <RadioButtonUncheckedIcon sx={{ color: "text.disabled", flexShrink: 0 }} />}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{modem.num_sim || "—"}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {[
                              modem.numero_chip && `Chip ${modem.numero_chip}`,
                              modem.estado,
                              servicio != null && `Servicio ${servicio}`,
                            ].filter(Boolean).join(" · ") || "—"}
                          </Typography>
                          {clientes.length > 0 && (
                            <Typography variant="caption" color="text.secondary" noWrap component="div" title={clientes.join(", ")}>
                              {clientes.length > 1 ? `${clientes.length} clientes: ` : "Cliente: "}{clientes.join(", ")}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {checked && (
                        <Stack spacing={1.25} sx={{ px: 1.5, pb: 1.5 }}>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            label="Observación (opcional)"
                            value={observaciones[id] || ""}
                            onChange={(e) => handleObservacionChange(modem.num_sim, e.target.value)}
                            placeholder="Estado del equipo, accesorios que faltan…"
                          />
                          <FotoField
                            id={`imagen-${id}`}
                            file={imagenes[id]}
                            onChange={(e) => handleImagenChange(modem.num_sim, e)}
                            onClear={() => setImagenes((prev) => { const n = { ...prev }; delete n[id]; return n; })}
                          />
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        {listo && (
          <Typography variant="body2" color={selectedModems.size ? "text.secondary" : "error"} sx={{ mr: "auto" }}>
            {selectedModems.size === 0
              ? "Elige al menos un equipo."
              : pideSucursal && sucursal === ""
                ? "Falta la sucursal."
                : selectedModems.size === 1 ? "1 equipo" : `${selectedModems.size} equipos`}
          </Typography>
        )}
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          {listo ? "Cancelar" : "Cerrar"}
        </Button>
        {listo && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!canConfirm}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AssignmentReturnIcon />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {saving ? "Guardando…" : getConfirmLabel(tipoDevolucion)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function Etiqueta({ children, sx }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: "block", fontWeight: 700, letterSpacing: 0.6, lineHeight: 1.6, mb: 1, ...sx }}
    >
      {children}
    </Typography>
  );
}
